'use client'

import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type { BondType } from '@moment/trading-types/reference-data'
import type { ProposalDetailsQueryParams } from '@/app/(dashboard)/proposals/[id]/page'
import type { RootState } from '@/app/store'
import type { LiveInventoryPostResponse } from '@/types/api/live-inventory'
import type { MarksApiResponse } from '@/types/api/prices'

import { ProposalStatus } from '@moment/horizon-data-access/proposal'
import { toError } from '@moment/observability/logger'
import { useCallback, useEffect, useMemo, useState } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { IconName } from '@/components/atomic/icon'
import ProgressBar from '@/components/atomic/progress-bar'
import SummaryBannerGrid from '@/components/atomic/summary-banner-grid'
import Tabs from '@/components/atomic/tabs'
import { BodySubtext } from '@/components/atomic/text'
import BondHeaderDetails from '@/components/molecules/bond-header-details'
import ConfirmOrderRemoveDialog from '@/components/molecules/confirm-proposal-order-remove-dialog'
import OrderBook from '@/components/molecules/order-book'
import PriceChart from '@/components/molecules/price-chart'
import { ProposalAllocationTable } from '@/components/molecules/proposal-allocation-table'
import ProposalBondDetails from '@/components/molecules/proposal-bond-details'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { useCancelProposalOrder } from '@/hooks/use-cancel-proposal-order'
import { useGetRatingsAccess } from '@/hooks/use-rating-columns'
import useUrlQueryParams from '@/hooks/use-url-query-params'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import { useGetPriceToYieldQuery } from '@/legacy-api/analytics-slice'
import {
  useGetLiveInventoryPostQuery,
  useInstrumentMarks,
} from '@/legacy-api/data-slice'
import {
  useGetProposalQuery,
  useUpdateProposalAllocationMutation,
  useUpdateProposalOrderMutation,
} from '@/legacy-api/proposals-slice'
import { createLogger } from '@/lib/logger'
import { OrderStatus } from '@/types/enums/order'
import isValidInstrumentIdFormat from '@/utils/instrument'
import { formatPercentage } from '@/utils/presentation/formatting'
import { calculateProportionalTaxEffectiveYtw } from '@/utils/proposals/calculate-yields'

import {
  getInventoryQueryParams,
  getSummaryItems,
  OrderDetails,
} from './helpers'
import {
  OrderStatusBarContainer,
  ProposalAllocationsContainer,
  ProposalOrderDetailsFooter,
  ProposalOrderPreviewContainer,
  ProposalOrderPreviewTabContentContainer,
  SummaryBannerContainer,
} from './styles'

enum ProposalOrderPreviewTabs {
  BondDetails = 'Bond details',
  PriceChart = 'Price chart',
  OrderBook = 'Order book',
  OrderTimeline = 'Order timeline',
}

enum AllocationsTabs {
  Allocations = 'Allocations',
}

interface ProposalOrderPreviewProps {
  proposalId: string
  replacementCandidates?: { order: ProposalOrder }[]
  selectedOrderId?: string
  disableQuantityEditing?: boolean
  hideCancelAllocation?: boolean
  onClose?: () => void
}

const logger = createLogger('ProposalOrderPreview')

interface UseReferenceDataProps {
  isin: string
  riskGroupId: string
}

interface UseReferenceDataReturn {
  referenceData: LiveInventoryPostResponse | undefined
  priceData: MarksApiResponse[] | undefined
}

/**
 * Custom hook to fetch reference data and price data for a given ISIN
 * @param props - Object containing ISIN and risk group ID
 * @returns Object containing reference data and price data
 */
function useReferenceData({
  isin,
  riskGroupId,
}: UseReferenceDataProps): UseReferenceDataReturn {
  // Fetch reference data from inventory
  const { data: referenceData } = useGetLiveInventoryPostQuery(
    getInventoryQueryParams(isin, riskGroupId),
  )

  const { data: priceData } = useInstrumentMarks(isin)

  return { referenceData, priceData }
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function ProposalOrderPreview({
  proposalId,
  replacementCandidates,
  selectedOrderId,
  disableQuantityEditing = false,
  hideCancelAllocation = false,
  onClose,
}: ProposalOrderPreviewProps) {
  const [inputPrice, setInputPrice] = useState<number | null>(null)

  const { riskGroupId } = useUserRiskGroupId()
  const { hasSPAccess, hasMoodysAccess } = useGetRatingsAccess()
  const { setQueryParams } = useUrlQueryParams<ProposalDetailsQueryParams>()

  const selectedOrder = useTieredSelector(
    (state: RootState) => state.proposals.selectedOrder,
    (params: ProposalDetailsQueryParams) => params.selectedOrder,
  )

  // Use selectedOrderId prop if provided, otherwise fall back to URL param
  const effectiveSelectedOrder = selectedOrderId || selectedOrder

  const { data: proposal } = useGetProposalQuery(
    { proposal_id: proposalId },
    { skip: !proposalId },
  )

  const proposalStatus = useMemo(() => {
    return proposal?.proposal_status
  }, [proposal])
  const isViewOnly = proposalStatus === ProposalStatus.view_only

  const order = useMemo(() => {
    // First try to find the order in the proposal's orders
    const proposalOrder = proposal?.orders.find(
      (order: ProposalOrder) =>
        order.moment_order_id === effectiveSelectedOrder,
    )
    if (proposalOrder) return proposalOrder

    // If not found in proposal orders, look in replacement candidates
    if (replacementCandidates) {
      const replacementOrder = replacementCandidates.find(
        (candidate) =>
          candidate.order.moment_order_id === effectiveSelectedOrder,
      )
      return replacementOrder?.order
    }

    return undefined
  }, [proposal, effectiveSelectedOrder, replacementCandidates])

  const isin = useMemo(() => order?.instrument.isin ?? '', [order])
  const { referenceData, priceData } = useReferenceData({ isin, riskGroupId })

  // Use the priceToYield endpoint with settlement date
  const {
    data: { yield_to_worst, yield_to_maturity } = {
      yield_to_worst: null,
      yield_to_maturity: null,
    },
  } = useGetPriceToYieldQuery(
    {
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      instrumentId: isin!,
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      price: inputPrice!,
    },
    {
      skip: !isin || !isValidInstrumentIdFormat(isin) || !inputPrice,
    },
  )
  const taxableEquivalentYieldToWorst = calculateProportionalTaxEffectiveYtw(
    order?.tax_effective_limit_ytw,
    order?.limit_ytw,
    yield_to_worst,
  )

  const summaryItems = useMemo(
    () =>
      getSummaryItems(referenceData, priceData, {
        hasSPAccess,
        hasMoodysAccess,
      }),
    [referenceData, priceData, hasSPAccess, hasMoodysAccess],
  )

  const fillPercentage = useMemo(
    () => ((order?.filled_quantity ?? 0) / (order?.quantity ?? 0)) * 100,
    [order],
  )

  const [updateProposalOrder] = useUpdateProposalOrderMutation()
  const [updateProposalAllocation] = useUpdateProposalAllocationMutation()

  const handleUpdateAllocation = (allocationId: string, quantity: number) => {
    try {
      updateProposalAllocation({
        proposal_id: proposal?.proposal_id ?? '',
        proposal_order_id: order?.moment_order_id ?? '',
        proposal_allocation_id: allocationId,
        quantity,
      })
    } catch (error) {
      logger.error('Failed to update allocation:', {
        error: toError(error),
        allocationId,
        quantity,
      })
    }
  }

  useEffect(() => {
    setInputPrice(order?.limit_price ?? null)
  }, [order?.limit_price])

  const handlePriceUpdate = useCallback(() => {
    try {
      if (!inputPrice || inputPrice <= 0) {
        throw new Error('Invalid price')
      }
      updateProposalOrder({
        proposal_id: proposal?.proposal_id ?? '',
        proposal_order_id: order?.moment_order_id ?? '',
        limit_price: inputPrice,
        limit_ytm: yield_to_maturity,
        limit_ytw: yield_to_worst,
        tax_effective_limit_ytw: taxableEquivalentYieldToWorst,
        post_tax_limit_ytw: yield_to_worst,
        instrument_id: order?.instrument.isin ?? '',
        allocations: order?.allocations ?? [],
      })
    } catch (error) {
      logger.error('Failed to update proposal order:', {
        error: toError(error),
        order,
        proposal,
      })
    }
  }, [
    proposal,
    order,
    inputPrice,
    taxableEquivalentYieldToWorst,
    yield_to_worst,
    yield_to_maturity,
    updateProposalOrder,
  ])

  const isProposalExecutingOrCompleted = useMemo(() => {
    return (
      proposalStatus === ProposalStatus.executing ||
      proposalStatus === ProposalStatus.completed
    )
  }, [proposalStatus])

  const [activeTab, setActiveTab] = useState<ProposalOrderPreviewTabs>(
    proposalStatus === ProposalStatus.executing
      ? ProposalOrderPreviewTabs.OrderTimeline
      : ProposalOrderPreviewTabs.BondDetails,
  )

  const tabs = useMemo(() => {
    if (isProposalExecutingOrCompleted) {
      return [
        ProposalOrderPreviewTabs.BondDetails,
        ProposalOrderPreviewTabs.OrderBook,
      ]
    }
    return [
      ProposalOrderPreviewTabs.BondDetails,
      ProposalOrderPreviewTabs.PriceChart,
      ProposalOrderPreviewTabs.OrderBook,
    ]
  }, [isProposalExecutingOrCompleted])

  const {
    handleRemoveOrder,
    handleRemoveClick,
    isRemoveDialogOpen,
    setIsRemoveDialogOpen,
    selectedTradeToRemove,
  } = useCancelProposalOrder({
    proposal,
    onOrderRemoved: () => {
      setQueryParams(
        { selectedOrder: undefined },
        'ProposalOrderPreview.handleOrderRemove',
      )
    },
  })

  if (!order) {
    return null
  }

  return (
    <ProposalOrderPreviewContainer>
      <div className="relative shrink-0 pl-4 pr-24 py-3">
        {onClose && (
          <Button
            className="absolute right-4 top-3 border-none dark:bg-moment-black-200"
            icon={<Icon iconName={IconName.XMarkIcon} size="medium" />}
            onClick={onClose}
            variant={ButtonVariant.Secondary}
          />
        )}
        <BondHeaderDetails
          bondTitle={order?.instrument.description_short || ''}
          id={order?.instrument.isin || ''}
          bondType={referenceData?.data[0]?.type as BondType}
          orderStatus={order?.status}
          isExecuting={isProposalExecutingOrCompleted}
        />
      </div>

      <SummaryBannerContainer>
        <SummaryBannerGrid summaryItems={summaryItems} columns={5} />
      </SummaryBannerContainer>
      {!isProposalExecutingOrCompleted && (
        <OrderDetails
          order={order}
          inputPrice={inputPrice}
          setInputPrice={setInputPrice}
          impliedYTW={yield_to_worst}
          handlePriceUpdate={handlePriceUpdate}
          isDisabled
        />
      )}
      {isProposalExecutingOrCompleted && (
        <OrderStatusBarContainer>
          <BodySubtext>Filled</BodySubtext>
          <ProgressBar value={fillPercentage} />
          <BodySubtext>
            {formatPercentage(fillPercentage, { numDigits: 0 })}
          </BodySubtext>
        </OrderStatusBarContainer>
      )}
      <Tabs
        baseTabsClassName="shrink-0 border-t border-primary-border"
        tabListClassName="pl-[16px]"
        tabs={tabs}
        defaultTab={ProposalOrderPreviewTabs.BondDetails}
        value={activeTab}
        onTabChange={(tab) => setActiveTab(tab as ProposalOrderPreviewTabs)}
      />
      <ProposalOrderPreviewTabContentContainer>
        {activeTab === ProposalOrderPreviewTabs.BondDetails && (
          <ProposalBondDetails isin={isin} />
        )}
        {activeTab === ProposalOrderPreviewTabs.PriceChart && (
          <PriceChart isin={isin} height={448} />
        )}
        {activeTab === ProposalOrderPreviewTabs.OrderBook && (
          <OrderBook
            baseClassName="h-full bg-secondary-background"
            instrumentId={order?.instrument.isin || ''}
          />
        )}
        {/* // TODO Hide order timeline for now until we have order ids from BE  */}
        {/* {activeTab === ProposalOrderPreviewTabs.OrderTimeline && (
          <OrderEventsTimeline
            tradingAndRfqEvents={tradingAndRfqEvents}
            isActiveOrder={isProposalExecutingOrCompleted}
            userArchetype={UserArchetype.Advisor}
          />
        )} */}
      </ProposalOrderPreviewTabContentContainer>
      <ProposalAllocationsContainer>
        <Tabs
          baseTabsClassName="border-t border-primary-border"
          tabListClassName="pl-[16px]"
          tabs={Object.values(AllocationsTabs)}
          defaultTab={AllocationsTabs.Allocations}
        />

        <ProposalAllocationTable
          allocations={order?.allocations || []}
          onUpdateAllocation={handleUpdateAllocation}
          proposalId={proposalId}
          disableQuantityEditing={disableQuantityEditing || isViewOnly}
          hideCancelAllocation={hideCancelAllocation || isViewOnly}
        />
      </ProposalAllocationsContainer>
      {selectedTradeToRemove && (
        <ConfirmOrderRemoveDialog
          open={isRemoveDialogOpen}
          onOpenChange={setIsRemoveDialogOpen}
          tradeDetails={selectedTradeToRemove}
          onConfirm={async () =>
            await handleRemoveOrder(selectedTradeToRemove.id)
          }
        />
      )}
      {(order?.status === OrderStatus.PendingExecution ||
        order?.status === OrderStatus.PartiallyFilled) && (
        <ProposalOrderDetailsFooter>
          <Button
            variant={ButtonVariant.Danger}
            label="Cancel order"
            onClick={() => handleRemoveClick(order?.moment_order_id ?? '')}
          />
        </ProposalOrderDetailsFooter>
      )}
    </ProposalOrderPreviewContainer>
  )
}
